import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import qs from 'qs';

import { setPortal } from 'modules/portal';
import { makeRowColumn, getHeadObj, dayColor } from 'lib/commonLib';
import { showSuccessSnackbar } from 'modules/snackbar';
import { addAllList } from 'modules/common';
import { contentsCategory } from 'lib/api/common';
import {
  audienceStatisticsData,
  audienceStatisticsSubData,
} from 'lib/api/datacenter';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';
import NewCategoryFilter from 'components/organisms/NewCategoryFilter';

import AudienceGraph from './AudienceGraph';

const AudienceStatistics = ({ displayFilterList }) => {
  const { t } = useTranslation();
  let initParams = {
    sDate: moment().add(-1, 'month').add(-1, 'day').format('YYYYMMDD'),
    eDate: moment().add(-1, 'day').format('YYYYMMDD'),
    sort: 'asc',
    order: 'CTGR_NM',
    adcFstRnk: 'all',
    pltfomTpCode: 'all',
    limit: 100,
  };
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const dispatch = useDispatch();
  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [contentCategory, setContentCategory] = useState([]);

  useEffect(() => {
    contentsCategory()
      .then((response) => {
        const { data } = response.data;
        setContentCategory(data);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  // header 및 body 컬럼 커스텀
  const customized = {
    ADC_SEQ: { merged: true },
    CTGR_NM: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CTGR_NM');
        return rowObj['isChild'] === 1
          ? dayColor(rowObj['STATS_DTTM'])
          : rowObj['CTGR_NM'] !== 'TOTAL'
          ? makeRowColumn(rowObj['CTGR_NM'], bodyItem1.type, bodyItem1.format)
          : 'total';
      },
      bodyStyle: {
        minWidth: 100,
      },
    },
    ADC_CF_USER_CNT: {
      makeHead: (headsMap) => {
        return t('유저카운트');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADC_CF_USER_CNT');
        return makeRowColumn(
          rowObj['ADC_CF_USER_CNT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
    ADC_CF_NEW_USER_CNT: {
      makeHead: (headsMap) => {
        return t('신규 유저카운트');
      },
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADC_CF_NEW_USER_CNT');
        return makeRowColumn(
          rowObj['ADC_CF_NEW_USER_CNT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
    },
    ORDER_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ORDER_CNT');
        let addParams = { ...params };
        let content;
        if (typeof rowObj['ADC_SEQ'] !== 'undefined') {
          addParams = { ...addParams, adcFstRnk: rowObj['ADC_SEQ'] };
        }
        addParams = qs.stringify(addParams);
        const showWindowUrl =
          '/report/datacenter/audience/conversion?isPop=1&' + addParams;

        if (rowObj['CTGR_NM'] !== 'TOTAL' && !rowObj['isChild']) {
          content = (
            <IconAreaWrap
              text={makeRowColumn(
                rowObj['ORDER_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
              icon={rowObj['IP'] !== 'total' && 'faExternalLink'}
              toolTipTitle={t('해당 카테고리의 컨버전 내역 팝업')}
              onClick={(e) => handlePortalUrl(showWindowUrl)}
            />
          );
        } else {
          content = makeRowColumn(
            rowObj['ORDER_CNT'],
            bodyItem1.type,
            bodyItem1.format
          );
        }
        return content;
      },
    },
    CLICK_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ORDER_CNT');
        let addParams = { ...params };
        let content;
        if (typeof rowObj['ADC_SEQ'] !== 'undefined') {
          addParams = { ...addParams, adcFstRnk: rowObj['ADC_SEQ'] };
        }
        addParams = qs.stringify(addParams);
        const showWindowUrl =
          '/report/datacenter/audience/adver?isPop=1&' + addParams;
        if (rowObj['CTGR_NM'] !== 'TOTAL' && !rowObj['isChild']) {
          content = (
            <IconAreaWrap
              text={makeRowColumn(
                rowObj['CLICK_CNT'],
                bodyItem1.type,
                bodyItem1.format
              )}
              icon={rowObj['IP'] !== 'total' && 'faExternalLink'}
              toolTipTitle={t('해당 카테고리의 오디언스 광고주 통계')}
              onClick={(e) => handlePortalUrl(showWindowUrl)}
            />
          );
        } else {
          content = makeRowColumn(
            rowObj['CLICK_CNT'],
            bodyItem1.type,
            bodyItem1.format
          );
        }
        return content;
      },
    },
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setParams(initParams);
      setInitialization(true);
    }
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  /**
   * 테이블 클릭 후 세부정보 받아올때 보낼 param setting 해주는 함수
   * @param {json} data 테이블 로우 데이터
   */
  const makeSubParam = (data) => {
    return {
      adcFstRnk: data.ADC_SEQ,
    };
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          displayFilterList={displayFilterList}
          useExternalFilter={false}
        />
        <FilterCollection
          label={t('컨텐츠 카테고리')}
          name="adcFstRnk"
          params={params}
          value={params.adcFstRnk}
          type="select"
          list={addAllList(contentCategory, 'CTGR_NM', 'CTGR_SEQ', 'all')}
          callBack={getParam}
          labelKey="CTGR_NM"
          valueKey="CTGR_SEQ"
        />
        <NewCategoryFilter params={params} getParam={getParam} />
      </NewFilterWrap>
      <AudienceGraph params={params} />
      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={audienceStatisticsData}
        dataDetailReqPromise={audienceStatisticsSubData}
        makeSubParam={makeSubParam}
        customized={customized}
        expandTable
        useSearch={false}
      />
    </>
  );
};

export default AudienceStatistics;
